import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Baby, Users } from "lucide-react";
import { useNannyMotherRoleSwitch } from "@/hooks/useNannyMotherRoleSwitch";
import { useTranslation } from "@/hooks/useTranslation";

interface NannyMotherRoleSwitchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetRole: 'nanny' | 'mother';
}

export const NannyMotherRoleSwitchDialog = ({
  isOpen,
  onClose,
  targetRole
}: NannyMotherRoleSwitchDialogProps) => {
  const { isSwitching, performRoleSwitch } = useNannyMotherRoleSwitch();
  const { t } = useTranslation('workspace');

  const handleConfirm = async () => {
    try {
      await performRoleSwitch(targetRole);
      onClose(); // Navigation happens in the hook
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const targetRoleName = targetRole === 'nanny' 
    ? t('switch_to_nanny_role', 'Nanny') 
    : t('switch_to_mother_role', 'Parent/Mother');

  const description = targetRole === 'nanny'
    ? t('role_switch_desc_to_nanny', 'Switching will remove Parent/Mother access and grant Nanny access.')
    : t('role_switch_desc_to_mother', 'Switching will remove Nanny access and grant Parent/Mother access.');

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isSwitching && !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            {targetRole === 'nanny' ? (
              <Baby className="w-6 h-6 text-primary" />
            ) : (
              <Users className="w-6 h-6 text-primary" />
            )}
            <DialogTitle>
              {t('role_switch_title', 'Confirm Role Switch')}
            </DialogTitle>
          </div>
          <DialogDescription className="space-y-4 pt-2">
            <p className="text-sm text-foreground">
              {description}
            </p>

            <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="text-xs text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">
                    {t('role_switch_note', 'What happens when you switch:')}
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>{t('role_switch_note_1', 'Your current role access will be removed')}</li>
                    <li>{t('role_switch_note_2', `You'll gain ${targetRoleName} role access`)}</li>
                    <li>{t('role_switch_note_3', 'You can switch back anytime')}</li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground italic">
              {t('role_switch_reversible', 'This change can be reversed later by switching back.')}
            </p>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSwitching}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSwitching}
          >
            {isSwitching 
              ? t('role_switch_processing', 'Switching...') 
              : t('confirm_and_switch', 'Confirm and Switch')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
